import Airtable from 'airtable';

// ----------------------------------------------------------------------
// ☁️ Airtable Service (Auto-Connected)
// ----------------------------------------------------------------------

// Configuration is now hardcoded/env-based to prevent user editing
export const getConfig = () => {
  return {
    // Priority: Env Var -> Hardcoded Fallback
    apiKey: import.meta.env.VITE_AIRTABLE_API_KEY || 'patFC3DmSN9ue3MLs.b36b744b79f01a6ced0aaddb62657578a1d6e306f524e86049e82b3ca29069e6',
    baseId: 'appCBNi6E4gSbOo5Z', // Corrected Base ID
    tableId: 'tblVwINCS2hyGHQLy'
  };
};

let base = null;
let currentTableId = null;

const configureBase = () => {
  const { apiKey, baseId, tableId } = getConfig();
  currentTableId = tableId;

  if (apiKey && baseId) {
    try {
      Airtable.configure({
        apiKey: apiKey,
        endpointUrl: 'https://api.airtable.com',
      });
      base = Airtable.base(baseId);
    } catch (e) {
      console.error("Airtable Configuration Error:", e);
      base = null;
    }
  } else {
    base = null;
  }
};

// Initialize immediately
configureBase();

export const isAirtableConfigured = () => !!base;

export const checkConnection = async () => {
  if (!base) return { success: false, message: 'Missing API Key or Base ID' };
  try {
    await base(currentTableId).select({ maxRecords: 1 }).firstPage();
    return { success: true, message: 'Connected' };
  } catch (error) {
    console.error("Airtable Connection Check Failed:", error);
    if (error.error === 'NOT_FOUND') return { success: false, message: `Table "${currentTableId}" not found.` };
    if (error.statusCode === 401) return { success: false, message: 'Unauthorized (Check API Key).' };
    return { success: false, message: 'Connection Failed' };
  }
};

// Fetch Workspaces & Include internal Airtable Record ID
export const fetchAllWorkspaces = async () => {
  if (!base) return [];
  try {
    const records = await base(currentTableId).select({
      sort: [{ field: 'title', direction: 'asc' }]
    }).all();

    return records.map(record => {
      try {
        const content = JSON.parse(record.get('content'));
        return {
          ...content,
          id: record.get('id'), // The Project/Workspace ID (e.g., ws-123)
          title: record.get('title'),
          source: 'cloud',
          _airtableId: record.id // CRITICAL: The internal Airtable Record ID (rec...)
        };
      } catch (e) {
        console.warn("Failed to parse record content", record.id);
        return null;
      }
    }).filter(w => w !== null);
  } catch (error) {
    console.error("Failed to fetch workspaces:", error);
    return [];
  }
};

// Save: Use internal ID if available to prevent duplicates
export const saveToAirtable = async (id, title, data, existingAirtableId = null) => {
  if (!base) return null;

  try {
    const contentString = JSON.stringify(data);
    const fields = { id, title, content: contentString };

    // Strategy 1: Update by Internal ID (Fastest & Safest)
    if (existingAirtableId) {
      try {
        await base(currentTableId).update(existingAirtableId, fields);
        return existingAirtableId;
      } catch (e) {
        // If internal ID fails (e.g. deleted), fall back to search
        console.warn("Internal ID update failed, falling back to search", e);
      }
    }

    // Strategy 2: Search by 'id' column (Fallback)
    const records = await base(currentTableId).select({
      filterByFormula: `{id} = '${id}'`,
      maxRecords: 1
    }).firstPage();

    if (records.length > 0) {
      await base(currentTableId).update(records[0].id, fields);
      return records[0].id;
    } else {
      const created = await base(currentTableId).create([{ fields }]);
      return created[0].id;
    }
  } catch (error) {
    console.error('Airtable Save Error:', error);
    throw error;
  }
};

// Delete from Airtable (Permanent)
export const deleteFromAirtable = async (airtableId) => {
  if (!base || !airtableId) return false;
  try {
    await base(currentTableId).destroy(airtableId);
    return true;
  } catch (error) {
    console.error('Airtable Delete Error:', error);
    return false;
  }
};

export const loadFromAirtable = async (id) => {
  if (!base) return null;
  try {
    const records = await base(currentTableId).select({
      filterByFormula: `{id} = '${id}'`,
      maxRecords: 1
    }).firstPage();

    if (records.length > 0) {
      const record = records[0];
      return {
        ...JSON.parse(record.get('content')),
        id: record.get('id'),
        title: record.get('title'),
        _airtableId: record.id
      };
    }
    return null;
  } catch (error) {
    console.error('Airtable Load Error:', error);
    return null;
  }
};